import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { passportInstance } from '../config/passport';
import NFTCard from '../components/nft/NFTCard';
import NFTMintModal from '../components/nft/NFTMintModal';
import NFTDetailsModal from '../components/nft/NFTDetailsModal';
import { FaEthereum } from 'react-icons/fa';
import { CubeIcon } from '@heroicons/react/24/outline';
import api from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';

// Mock NFT collection data - will be replaced with actual API data
const MOCK_COLLECTION = {
  name: "BitSport Genesis Collection",
  description: "The official BitSport Genesis NFT Collection on Immutable X. These NFTs grant exclusive access to premium features, tournaments, and rewards within the BitSport ecosystem.",
  totalSupply: 1000,
  mintedCount: 342,
  price: 0.08,
  currency: "ETH",
  creator: "BitSport",
  releaseDate: "2023-07-15",
  bannerImage: "/nft/collection-banner.jpg",
  logoImage: "/nft/collection-logo.png",
};

// Mock NFT items - will be replaced with actual API data
const MOCK_NFTS = [
  {
    id: "1",
    name: "BitSport Champion #001",
    description: "Legendary champion with exclusive tournament access",
    image: "/nft/nft-1.jpg",
    rarity: "Legendary",
    attributes: [
      { trait_type: "Strength", value: 95 },
      { trait_type: "Agility", value: 80 },
      { trait_type: "Intelligence", value: 90 },
      { trait_type: "Luck", value: 85 },
    ],
    price: 0.15,
    currency: "ETH",
    owner: null,
  },
  {
    id: "2",
    name: "BitSport Strategist #042",
    description: "Master strategist with enhanced prediction capabilities",
    image: "/nft/nft-2.jpg",
    rarity: "Epic",
    attributes: [
      { trait_type: "Strength", value: 70 },
      { trait_type: "Agility", value: 75 },
      { trait_type: "Intelligence", value: 98 },
      { trait_type: "Luck", value: 65 },
    ],
    price: 0.12,
    currency: "ETH",
    owner: null,
  },
  {
    id: "3",
    name: "BitSport Speedster #103",
    description: "Lightning-fast competitor with reaction time bonuses",
    image: "/nft/nft-3.jpg",
    rarity: "Rare",
    attributes: [
      { trait_type: "Strength", value: 65 },
      { trait_type: "Agility", value: 99 },
      { trait_type: "Intelligence", value: 75 },
      { trait_type: "Luck", value: 70 },
    ],
    price: 0.1,
    currency: "ETH",
    owner: null,
  },
  {
    id: "4",
    name: "BitSport Lucky #217",
    description: "Fortune favors this character with enhanced rewards",
    image: "/nft/nft-4.jpg",
    rarity: "Uncommon",
    attributes: [
      { trait_type: "Strength", value: 60 },
      { trait_type: "Agility", value: 65 },
      { trait_type: "Intelligence", value: 70 },
      { trait_type: "Luck", value: 99 },
    ],
    price: 0.08,
    currency: "ETH",
    owner: null,
  },
  {
    id: "5",
    name: "BitSport Warrior #335",
    description: "Powerful warrior with enhanced combat abilities",
    image: "/nft/nft-5.jpg",
    rarity: "Rare",
    attributes: [
      { trait_type: "Strength", value: 95 },
      { trait_type: "Agility", value: 85 },
      { trait_type: "Intelligence", value: 65 },
      { trait_type: "Luck", value: 70 },
    ],
    price: 0.1,
    currency: "ETH",
    owner: null,
  },
  {
    id: "6",
    name: "BitSport Mystic #429",
    description: "Mystical character with special abilities",
    image: "/nft/nft-6.jpg",
    rarity: "Epic",
    attributes: [
      { trait_type: "Strength", value: 70 },
      { trait_type: "Agility", value: 75 },
      { trait_type: "Intelligence", value: 90 },
      { trait_type: "Luck", value: 85 },
    ],
    price: 0.12,
    currency: "ETH",
    owner: null,
  },
];

export default function NFTCollection() {
  const { user, isConnected } = useAuth();
  const [collection, setCollection] = useState(MOCK_COLLECTION);
  const [nfts, setNfts] = useState(MOCK_NFTS);
  const [loading, setLoading] = useState(true);
  const [showMintModal, setShowMintModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedNft, setSelectedNft] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    // Fetch collection data
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('Starting to fetch NFT collection data...');
        console.log('API endpoint for collections:', API_ENDPOINTS.nft.collections);

        // Fetch collections
        const collectionsResponse = await api.get(API_ENDPOINTS.nft.collections);
        console.log('Collections response (raw):', collectionsResponse);

        // Check if we have any collections at all
        const hasDirectCollections = collectionsResponse && Array.isArray(collectionsResponse) && collectionsResponse.length > 0;
        const hasNestedCollections = collectionsResponse && collectionsResponse.data && Array.isArray(collectionsResponse.data) && collectionsResponse.data.length > 0;

        console.log('Has direct collections array:', hasDirectCollections);
        console.log('Has nested collections array:', hasNestedCollections);

        if (hasDirectCollections) {
          // Get the first active collection
          const activeCollection = collectionsResponse.find(c => c.status === 'active');
          console.log('Active collections found (direct):', collectionsResponse.filter(c => c.status === 'active').length);
          console.log('Selected collection:', activeCollection || collectionsResponse[0]);

          if (activeCollection) {
            setCollection(activeCollection);
          } else {
            console.log('No active collections found, using first available collection');
            setCollection(collectionsResponse[0]);
          }

          // Fetch NFTs for this collection
          const collectionToUse = activeCollection || collectionsResponse[0];
          console.log('Fetching NFTs for collection:', collectionToUse.name, collectionToUse._id);
          console.log('NFT endpoint:', API_ENDPOINTS.nft.nfts(collectionToUse._id));

          try {
            const nftsResponse = await api.get(API_ENDPOINTS.nft.nfts(collectionToUse._id));
            console.log('NFTs response (raw):', nftsResponse);

            // Check if we have any NFTs at all
            const hasDirectNFTs = nftsResponse && Array.isArray(nftsResponse) && nftsResponse.length > 0;
            const hasNestedNFTs = nftsResponse && nftsResponse.data && Array.isArray(nftsResponse.data) && nftsResponse.data.length > 0;

            console.log('Has direct NFTs array:', hasDirectNFTs);
            console.log('Has nested NFTs array:', hasNestedNFTs);

            if (hasDirectNFTs) {
              console.log('Found NFTs (direct):', nftsResponse);
              console.log('NFT statuses:', nftsResponse.map(nft => nft.status).join(', '));
              setNfts(nftsResponse);
            } else if (hasNestedNFTs) {
              console.log('Found NFTs (from data property):', nftsResponse.data);
              console.log('NFT statuses:', nftsResponse.data.map(nft => nft.status).join(', '));
              setNfts(nftsResponse.data);
            } else {
              // If no NFTs found, create an empty array
              console.log('No NFTs found for this collection');
              setNfts([]);
              toast.warning('No NFTs available in this collection yet. Make sure NFTs have status "minting", "minted", "transferred", or "sold".', {
                duration: 6000,
                icon: '📝',
                style: {
                  borderRadius: '10px',
                  background: '#333',
                  color: '#fff',
                },
              });
            }
          } catch (error) {
            console.error('Error fetching NFTs:', error);
            toast.error('Failed to fetch NFTs for this collection');
            setNfts([]);
          }
        } else if (hasNestedCollections) {
          // Alternative response format
          const activeCollection = collectionsResponse.data.find(c => c.status === 'active');
          console.log('Active collections found (nested):', collectionsResponse.data.filter(c => c.status === 'active').length);
          console.log('Selected collection:', activeCollection || collectionsResponse.data[0]);

          if (activeCollection) {
            setCollection(activeCollection);
          } else {
            console.log('No active collections found, using first available collection');
            setCollection(collectionsResponse.data[0]);
          }

          // Fetch NFTs for this collection
          const collectionToUse = activeCollection || collectionsResponse.data[0];
          console.log('Fetching NFTs for collection:', collectionToUse.name, collectionToUse._id);
          console.log('NFT endpoint:', API_ENDPOINTS.nft.nfts(collectionToUse._id));

          try {
            const nftsResponse = await api.get(API_ENDPOINTS.nft.nfts(collectionToUse._id));
            console.log('NFTs response (raw):', nftsResponse);

            // Check if we have any NFTs at all
            const hasDirectNFTs = nftsResponse && Array.isArray(nftsResponse) && nftsResponse.length > 0;
            const hasNestedNFTs = nftsResponse && nftsResponse.data && Array.isArray(nftsResponse.data) && nftsResponse.data.length > 0;

            console.log('Has direct NFTs array:', hasDirectNFTs);
            console.log('Has nested NFTs array:', hasNestedNFTs);

            if (hasDirectNFTs) {
              console.log('Found NFTs (direct):', nftsResponse);
              console.log('NFT statuses:', nftsResponse.map(nft => nft.status).join(', '));
              setNfts(nftsResponse);
            } else if (hasNestedNFTs) {
              console.log('Found NFTs (from data property):', nftsResponse.data);
              console.log('NFT statuses:', nftsResponse.data.map(nft => nft.status).join(', '));
              setNfts(nftsResponse.data);
            } else {
              // If no NFTs found, create an empty array
              console.log('No NFTs found for this collection');
              setNfts([]);
              toast.warning('No NFTs available in this collection yet. Make sure NFTs have status "minting", "minted", "transferred", or "sold".', {
                duration: 6000,
                icon: '📝',
                style: {
                  borderRadius: '10px',
                  background: '#333',
                  color: '#fff',
                },
              });
            }
          } catch (error) {
            console.error('Error fetching NFTs:', error);
            toast.error('Failed to fetch NFTs for this collection');
            setNfts([]);
          }
        } else {
          // If no collections found, show a message and set empty arrays
          console.log('No active collections found, showing empty state');
          toast.error('No active NFT collections available. Please set a collection to "active" status in the admin panel.', {
            duration: 6000,
            icon: '🎮',
            style: {
              borderRadius: '10px',
              background: '#333',
              color: '#fff',
            },
          });
          // Create a minimal collection object with required fields
          setCollection({
            name: "No Collection Available",
            description: "There are no active NFT collections available at this time.",
            totalSupply: 0,
            mintedCount: 0,
            price: 0,
            bannerImage: "/banner.png",
            logoImage: "/logo.png",
          });
          setNfts([]);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching NFT data:', error);
        toast.error('Failed to load NFT collection data');
        // Set empty state instead of mock data
        setCollection({
          name: "Error Loading Collection",
          description: "There was an error loading the NFT collection data. Please try again later.",
          totalSupply: 0,
          mintedCount: 0,
          price: 0,
          bannerImage: "/banner.png",
          logoImage: "/logo.png",
        });
        setNfts([]);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const [draftNFTs, setDraftNFTs] = useState([]);

  const handleMint = async (quantity) => {
    if (!isConnected) {
      toast.error('Please connect your wallet to mint NFTs');
      return;
    }

    if (!collection || !collection._id) {
      toast.error('Collection information is missing');
      return;
    }

    try {
      // Show loading toast
      const loadingToastId = toast.loading('Preparing to mint NFT...');

      try {
        // Get available NFTs for minting
        const availableNFTs = await api.get(API_ENDPOINTS.nft.nfts(collection._id));

        // Filter for draft NFTs that can be minted
        const draftNFTs = availableNFTs.data.filter(nft => nft.status === 'draft');
        setDraftNFTs(draftNFTs);

        if (draftNFTs.length === 0) {
          toast.dismiss(loadingToastId);
          toast.error('No NFTs available for minting');
          return;
        }

        // The actual minting will be handled by the ImmutableMintButton component
        toast.dismiss(loadingToastId);

        // Show the mint modal with the draft NFTs
        setShowMintModal(true);
      } catch (innerError) {
        toast.dismiss(loadingToastId);
        console.error('Error during minting preparation:', innerError);
        toast.error('Failed to prepare for minting. Please try again.');
      }
    } catch (error) {
      console.error('Error preparing to mint NFT:', error);
      toast.error('Failed to prepare for minting. Please try again.');
    }
  };

  // Function to refresh NFT data after minting
  const refreshNFTData = async () => {
    try {
      // Refresh the NFT list
      const updatedNFTs = await api.get(API_ENDPOINTS.nft.nfts(collection._id));
      setNfts(updatedNFTs.data);

      // Also update the collection to get the updated mintedCount
      const updatedCollection = await api.get(API_ENDPOINTS.nft.collection(collection._id));
      if (updatedCollection.data) {
        setCollection(updatedCollection.data);
      }

      // Close the mint modal
      setShowMintModal(false);
    } catch (error) {
      console.error('Error refreshing NFT data:', error);
      toast.error('Failed to refresh NFT data. Please reload the page.');
    }
  };

  const handleNftClick = (nft) => {
    // This function is called by the Quick View button in NFTCard
    // It should open the modal, not navigate to the details page
    setSelectedNft(nft);
    setShowDetailsModal(true);
  };

  const filteredNfts = filter === 'all'
    ? nfts
    : nfts.filter(nft => nft.rarity && nft.rarity.toLowerCase() === filter.toLowerCase());

  return (
    <div className="min-h-screen bg-dark">
      {/* Hero Section with Parallax Effect */}
      <div className="relative h-[50vh] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-dark z-10"></div>
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ y: [0, -15, 0], scale: 1 }}
          transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0"
        >
          <img
            src={collection.bannerImage || "/banner.png"}
            alt={collection.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/banner.png";
            }}
          />
        </motion.div>
        <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 neon-text">
              {collection.name}
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto px-4">
              {collection.description}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Collection Stats */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="glass-panel p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div className="p-4">
              <h3 className="text-gray-400 text-sm mb-1">Total Supply</h3>
              <p className="text-3xl font-bold neon-text">{collection.totalSupply}</p>
            </div>
            <div className="p-4">
              <h3 className="text-gray-400 text-sm mb-1">Minted</h3>
              <p className="text-3xl font-bold neon-text">{collection.mintedCount}</p>
            </div>
            <div className="p-4">
              <h3 className="text-gray-400 text-sm mb-1">Floor Price</h3>
              <p className="text-3xl font-bold neon-text flex items-center justify-center">
                {collection.price} <FaEthereum className="ml-1" />
              </p>
            </div>
            <div className="p-4">
              <button
                onClick={() => setShowMintModal(true)}
                className="glow-button w-full flex items-center justify-center"
              >
                <CubeIcon className="h-5 w-5 mr-2" /> Mint NFT
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-violet-600 text-white'
                : 'bg-dark-lighter text-gray-300 hover:bg-dark-light'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('legendary')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'legendary'
                ? 'bg-yellow-600 text-white'
                : 'bg-dark-lighter text-gray-300 hover:bg-dark-light'
            }`}
          >
            Legendary
          </button>
          <button
            onClick={() => setFilter('epic')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'epic'
                ? 'bg-purple-600 text-white'
                : 'bg-dark-lighter text-gray-300 hover:bg-dark-light'
            }`}
          >
            Epic
          </button>
          <button
            onClick={() => setFilter('rare')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'rare'
                ? 'bg-blue-600 text-white'
                : 'bg-dark-lighter text-gray-300 hover:bg-dark-light'
            }`}
          >
            Rare
          </button>
          <button
            onClick={() => setFilter('uncommon')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === 'uncommon'
                ? 'bg-green-600 text-white'
                : 'bg-dark-lighter text-gray-300 hover:bg-dark-light'
            }`}
          >
            Uncommon
          </button>
        </div>

        {/* NFT Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-violet-500"></div>
          </div>
        ) : filteredNfts.length === 0 ? (
          <div className="flex flex-col justify-center items-center py-20">
            <div className="text-2xl text-gray-400 mb-4">No NFTs found</div>
            <p className="text-gray-500 text-center max-w-md">
              {filter !== 'all'
                ? `There are no NFTs with ${filter} rarity in this collection.`
                : 'This collection does not have any NFTs yet.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredNfts.map((nft) => (
              <NFTCard
                key={nft._id || nft.id}
                nft={nft}
                onClick={handleNftClick} // This will be used for the Quick View button
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <NFTMintModal
        isOpen={showMintModal}
        onClose={() => setShowMintModal(false)}
        onMint={refreshNFTData}
        collection={{
          ...collection,
          draftNFTs: draftNFTs
        }}
      />

      <NFTDetailsModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        nft={selectedNft}
      />
    </div>
  );
}
